"use client";

import { ArrowSwapHorizontal } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import type React from "react";
import { useState } from "react";
import { Skeleton } from "@/components/ui";
import { Button } from "@/components/ui/button";
import {
  formatPriceAsIrt,
  usePriceConverter,
} from "@/hooks/price/usePriceConverter";
import { cn } from "@/lib/utils";

interface ProductPriceSwitchProps {
  seller_share_unit?: number | string | null;
  seller_share_currency?: string | null;
  seller_settlement_currency?: string | null;
  bv?: number | string | null;
  className?: string;
  /** Optional commission rate to deduct from SV (e.g. 0.03 for 3%) */
  commissionRate?: number;
}

/**
 * Component to display product price with ability to switch between
 * settlement currency (default) and IRT equivalent
 * Shows: BV UVM + SV in settlement currency (USDT or UVM) by default
 * Can switch to IRT equivalent by clicking the swap button
 */
export const ProductPriceSwitch = ({
  seller_share_unit,
  seller_share_currency,
  seller_settlement_currency,
  bv,
  className,
  commissionRate,
}: ProductPriceSwitchProps): React.ReactNode => {
  const priceConverter = usePriceConverter();
  const tCurrency = useTranslations("common.currency");
  const [showIrt, setShowIrt] = useState(false);

  if (!seller_share_unit || !seller_share_currency) return null;

  // Normalize settlement currency - default to UVM if not provided
  const normalizeSettlement = (
    currency: string | null | undefined,
  ): "USDT" | "UVM" => {
    if (!currency) return "UVM";
    const normalized = currency.toUpperCase();
    if (normalized === "USDT") return "USDT";
    return "UVM";
  };
  const settlementCurrency = normalizeSettlement(seller_settlement_currency);

  if (priceConverter.isLoading || !priceConverter.rates) {
    return <Skeleton variant="muted" className={className || "h-4 w-24"} />;
  }

  // Convert price to settlement currency
  const { svInSettlementCurrency } = priceConverter.convertToSettlementCurrency(
    {
      seller_share_unit,
      seller_share_currency,
      bv,
    },
    settlementCurrency,
  );

  const bvValue = typeof bv === "string" ? parseFloat(bv) : bv || 0;
  const hasBv = bvValue > 0;

  // Apply commission deduction if provided
  const finalSv = commissionRate
    ? svInSettlementCurrency * (1 - commissionRate)
    : svInSettlementCurrency;

  // Format SV with smart decimal display
  const formattedSv = (() => {
    const rounded = Math.round(finalSv * 100) / 100;
    const decimalPart = rounded % 1;
    const formattedValue =
      decimalPart === 0 || decimalPart < 0.1
        ? Math.round(rounded).toString()
        : rounded.toFixed(2);
    return formattedValue;
  })();

  // Format BV with smart decimal display
  const formattedBv = (() => {
    const rounded = Math.round(bvValue * 100) / 100;
    const decimalPart = rounded % 1;
    return decimalPart === 0 || decimalPart < 0.1
      ? Math.round(rounded).toString()
      : rounded.toFixed(2);
  })();

  const handleToggle = () => {
    setShowIrt((prev) => !prev);
  };

  // Render settlement currency price
  const renderSettlementPrice = () => (
    <div dir="ltr" className="flex items-center gap-0.5 font-inter">
      <span>{formattedSv}</span>
      <span>{settlementCurrency}</span>
      {hasBv && (
        <>
          <span>+</span>
          <span>{formattedBv}</span>
          <span>UVM</span>
        </>
      )}
    </div>
  );

  // Render IRT price
  const renderIrtPrice = () => {
    if (!priceConverter.rates) return null;
    // Apply commission to the raw unit before IRT conversion
    const rawUnit =
      typeof seller_share_unit === "string"
        ? parseFloat(seller_share_unit)
        : seller_share_unit || 0;
    const adjustedUnit = commissionRate
      ? rawUnit * (1 - commissionRate)
      : rawUnit;
    const irtContent = formatPriceAsIrt(
      {
        seller_share_unit: adjustedUnit,
        seller_share_currency,
        bv,
        hideIcon: false,
      },
      priceConverter.rates,
      tCurrency("toman"),
    );

    return <div>{irtContent}</div>;
  };

  return (
    <div className={cn("flex flex-row-reverse items-center gap-2", className)}>
      <div className="flex-1 min-w-0">
        {showIrt ? renderIrtPrice() : renderSettlementPrice()}
      </div>
      <Button
        variant="light"
        size="icon"
        radius="full"
        onClick={handleToggle}
        className="h-6 w-6 p-0 shrink-0"
        aria-label={
          showIrt ? "Show settlement currency" : "Show IRT equivalent"
        }
      >
        <ArrowSwapHorizontal size={16} />
      </Button>
    </div>
  );
};
